import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUploadFiles } from "api/image.api";
import {
  createUppdragFiles,
  deleteUppdragFile,
  patchUppdrag,
  sendUppdragAttachments,
  UppdragQueryKey,
} from "api/uppdrag.api";
import { FileUploadButton } from "components/buttons/FileUploadButton";
import { useEffect, useState } from "react";
import { InspectionType } from "types/enums";
import {
  shouldShowEmailToParentCustomerPing,
  Uppdrag,
  UppdragStatus,
} from "types/uppdrag.type";
import Alert from "components/Alert";
import { useCustomerDocumentationEmail } from "components/inspection/attachments/useCustomerDocumentationEmail.logic";
import Modal from "components/Modal";
import { DocumentationEmailForm } from "components/DocumentationEmailForm";
import { usePreviewPdf } from "hooks/usePreviewPdf";
import PrimaryButton from "components/buttons/PrimaryButton";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
  ContentContainer,
  HeaderContainer,
  ShadowCard,
} from "components/ShadowCard";
import { Headline4 } from "components/typography/Headline4";
import {
  FilePreviewSmall,
  InspectionPDFPreviewSmall,
} from "components/FilePreviewSmall";
import { Headline5 } from "components/typography/Headline5";
import { getFileNameNew, splitFileName } from "helpers/file";
import { HasSentStatus } from "types/transportDocument.type";
import { SentToCustomerStatusTag } from "components/SentToCustomerStatusTag";
import SecondaryButton from "components/buttons/SecondaryButton";
import { InspectionItem } from "./InspectionItem";
import { DotMenu } from "components/DotMenu";
import { Ping } from "components/Ping";
import { Info } from "components/Info";
import { useNotification } from "hooks/useNotification";
import { useIsAdmin } from "store/user.store";
import { Headline6 } from "components/typography/Headline6";
import { RapporteringsTyp } from "api/transportDocument.api";

type Props = {
  uppdrag: Uppdrag;
};
export const ParentCustomer = ({ uppdrag }: Props) => {
  const isAdmin = useIsAdmin();
  const [latestFileName, setLatestFileName] = useState<string | undefined>();
  const qc = useQueryClient();
  const createUppdragFilesMutation = useMutation({
    ...createUppdragFiles(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });
  const uploadFilesMutation = useUploadFiles();

  useEffect(() => {
    if (uploadFilesMutation.data) {
      const [nameWithoutExtension, extension] = splitFileName(latestFileName);
      createUppdragFilesMutation.mutate({
        uppdragId: uppdrag.id,
        payload: {
          files: uploadFilesMutation.data.map((x) => ({
            id: undefined,
            name: x,
            url: x,
            nameWithoutExtension,
            extension,
          })),
        },
      });
      uploadFilesMutation.reset();
    }
  }, [
    createUppdragFilesMutation,
    latestFileName,
    uploadFilesMutation,
    uppdrag.id,
  ]);

  const onFileAdd = async (blob: Blob | null, filename: string) => {
    setLatestFileName(filename);

    if (!blob) return;
    const file = new File([blob], filename);
    if (!file) {
      return;
    }

    uploadFilesMutation.mutate([file]);
  };

  const deleteMutation = useMutation({
    ...deleteUppdragFile(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });

  /// DocumentationEmail
  const {
    isDocumentationModalOpen,
    setIsDocumentationModalOpen,
    closeModal,
    onSubmit,
    register,
    errors,
    isPatchingCustomer,
  } = useCustomerDocumentationEmail(uppdrag.parentCustomer!);
  // } = useCustomerDocumentationEmail(
  //   (inspection.assignment.isProxyAssignment
  //     ? inspection.assignment.parentCustomer
  //     : inspection.separator.customer) as Customer,
  //   inspection.id
  // );

  const { previewPdf, isLoading, currentUrl } = usePreviewPdf();

  const isReadyToSendForFirstTime =
    (uppdrag.emailToParentCustomerStatus === HasSentStatus.NotSent ||
      uppdrag.emailToParentCustomerStatus === HasSentStatus.Ignored) &&
    uppdrag.status === UppdragStatus.Completed &&
    !!uppdrag.parentCustomer?.documentationEmail;

  const isReadyToSendForSecondTime =
    uppdrag.emailToParentCustomerStatus === HasSentStatus.Sent &&
    uppdrag.status === UppdragStatus.Completed &&
    !!uppdrag.parentCustomer?.documentationEmail;

  const notification = useNotification();
  const sendMutation = useMutation({
    ...sendUppdragAttachments(),
    onSuccess: () => {
      notification.info("Bilagor skickade");
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });

  const patchUppdragMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
    },
  });

  const hasDocumentationEmailButNotReady =
    !!uppdrag.parentCustomer!.documentationEmail &&
    uppdrag.status !== UppdragStatus.Completed;

  return (
    <ShadowCard>
      <HeaderContainer>
        <Headline4 className="flex items-center">
          Till huvudkund
          {shouldShowEmailToParentCustomerPing(uppdrag) && <Ping />}
          {uppdrag.emailToParentCustomerStatus === HasSentStatus.Sent && (
            <SentToCustomerStatusTag
              className="ml-3"
              status={HasSentStatus.Sent}
            />
          )}
        </Headline4>
        {shouldShowEmailToParentCustomerPing(uppdrag) && (
          <DotMenu
            items={[
              {
                label: "Ta bort påminnelse",
                onClick: () =>
                  patchUppdragMutation.mutate({
                    id: uppdrag.id,
                    payload: {
                      emailToParentCustomerStatus: HasSentStatus.Ignored,
                    },
                  }),
              },
            ]}
          />
        )}
      </HeaderContainer>
      <ContentContainer className="space-y-8">
        {uppdrag.fiveYearsInspectionList.map((x) => (
          <InspectionItem
            key={x.id}
            inspection={x}
            type={InspectionType.FiveYears}
            uppdrag={uppdrag}
          />
        ))}
        {uppdrag.sixMonthsInspectionList.map((x) => (
          <InspectionItem
            key={x.id}
            inspection={x}
            type={InspectionType.SixMonths}
            uppdrag={uppdrag}
          />
        ))}
        {uppdrag.transportSedelList.length > 0 && (
          <div className="flex flex-col">
            <Headline5 className="mb-1">Transportsedel</Headline5>
            {uppdrag.transportSedelList.map((x) => (
              <InspectionPDFPreviewSmall
                key={x.id}
                inspectionName="transportsedel.pdf"
                isLoading={
                  isLoading &&
                  currentUrl === `/transport-sedel/${x.id}/preview-pdf`
                }
                onClick={() =>
                  previewPdf(`/transport-sedel/${x.id}/preview-pdf`)
                }
              />
            ))}
          </div>
        )}
        {(uppdrag.ombudsRapporteringList.length > 0 ||
          uppdrag.transportRapporteringList.length > 0) && (
          <div className="flex flex-col space-y-2">
            <Headline6>
              {uppdrag.ombudsRapporteringList.length > 1
                ? "Rapporteringar till Naturvårdsverket"
                : "Rapportering till Naturvårdsverket"}
            </Headline6>
            {uppdrag.transportRapporteringList.map((x) => (
              <InspectionPDFPreviewSmall
                key={x.id}
                inspectionName={`transport_${x.nvAvfallId}.pdf`}
                isLoading={
                  isLoading &&
                  currentUrl ===
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.Transport}`
                }
                onClick={() =>
                  previewPdf(
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.Transport}`,
                  )
                }
              />
            ))}
            {uppdrag.ombudsRapporteringList.map((x) => (
              <InspectionPDFPreviewSmall
                key={x.id}
                inspectionName={`avfallsproducent_${x.nvAvfallId}.pdf`}
                isLoading={
                  isLoading &&
                  currentUrl ===
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.OmbudsRapportering}`
                }
                onClick={() =>
                  previewPdf(
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.OmbudsRapportering}`,
                  )
                }
              />
            ))}
          </div>
        )}
        <h5 className="text-sm">Övriga filer</h5>
        {uppdrag.fileList.map((x) => (
          <FilePreviewSmall
            key={x.id}
            url={x.url}
            onDelete={() => {
              deleteMutation.mutate({ uppdragId: uppdrag.id, fileId: x.id });
            }}
            name={getFileNameNew(x)}
          />
        ))}
        <FileUploadButton
          onFileAdd={onFileAdd}
          isLoading={uploadFilesMutation.isPending}
        />
        {!uppdrag.parentCustomer?.documentationEmail && isAdmin && (
          <div className="mt-6">
            <Alert
              text={`${uppdrag.parentCustomer!.name} saknar Epostadress för rapporter.`}
              buttonText="Lägg till"
              onButtonClick={() => setIsDocumentationModalOpen(true)}
            />
          </div>
        )}
        {hasDocumentationEmailButNotReady && isAdmin && (
          <Info
            text={"Uppdraget behöver slutföras innan bilagorna kan skickas"}
          />
        )}
        {isReadyToSendForFirstTime && isAdmin && (
          <div className="flex flex-col items-center space-y-2">
            <PrimaryButton
              onClick={() =>
                sendMutation.mutate({
                  uppdragId: uppdrag.id,
                  payload: {
                    toParentCustomer: true,
                  },
                })
              }
              isLoading={sendMutation.isPending}
              loadingText="Skickar..."
              className="w-full"
            >
              <EnvelopeIcon className="mr-2 h-5 w-5" />
              {`Skicka till ${uppdrag.parentCustomer!.name}`}
            </PrimaryButton>
            <span className="text-xs text-gray-500">
              Skickas till
              <span className="ml-1 font-medium text-gray-900">
                {uppdrag.parentCustomer!.documentationEmail}
              </span>
            </span>
          </div>
        )}

        {isReadyToSendForSecondTime && isAdmin && (
          <div className="flex flex-col items-center space-y-2">
            <SecondaryButton
              onClick={() =>
                sendMutation.mutate({
                  uppdragId: uppdrag.id,
                  payload: {
                    toParentCustomer: true,
                  },
                })
              }
              isLoading={sendMutation.isPending}
              loadingText="Skickar..."
              className="w-full"
            >
              <EnvelopeIcon className="mr-2 h-5 w-5" />
              Skicka igen
            </SecondaryButton>
            <span className="text-xs text-gray-500">
              Skickas till
              <span className="ml-1 font-medium text-gray-900">
                {uppdrag.parentCustomer!.documentationEmail}
              </span>
            </span>
          </div>
        )}

        <Modal
          isOpen={isDocumentationModalOpen}
          onClose={closeModal}
          title="Ändra kund"
          actionText="Spara"
          actionHandler={onSubmit}
          isLoading={isPatchingCustomer}
          loadingText="Sparar..."
        >
          <DocumentationEmailForm
            register={register}
            errors={errors}
            customerName={uppdrag.parentCustomer!.name}
          />
        </Modal>
      </ContentContainer>
    </ShadowCard>
  );
};
